import { strings } from "core/strings";
import { guid } from "core/types/Guid";
import { yupExtensions } from "core/validation/yupExtensions";
import * as yup from "yup";

import { DATE_FORMATS, DateTime } from "@bps/date-time";

import { Address } from "./Address";
import { Country } from "./booking";

export interface Person {
  id: guid | null;
  firstName: string;
  lastName: string;
  dateOfBirth?: DateTime;
  mobile: string;
  address: Address;
}

export const PersonMeta = {
  getDisplayName: (person: Person) =>
    `${person.firstName || ""} ${person.lastName || ""}`.trim(),
  getInitials: (person: Person) =>
    `${person.firstName[0] || ""}${person.lastName[0] || ""}`.trim(),

  // The oldest person ever to have lived was 122
  getMinDateOfBirth: () => DateTime.now().minus({ years: 125 }).startOf("day"),

  // Users must be at least 14 years old.
  getMaxDateOfBirthForUserProfile: () =>
    DateTime.now().minus({ years: 14 }).startOf("day"),

  sort: (left: Person, right: Person) => {
    const lastNameResult = strings.compare(left.lastName, right.lastName);
    return lastNameResult === 0
      ? strings.compare(left.firstName, right.firstName)
      : lastNameResult;
  }
};

/**
 * Validation schema for the required fields for a person - firstName, lastName and dateOfBirth.
 */

export const PersonValidationSchema = yup.object().shape({
  firstName: yup
    .string()
    .required("Please enter a first name")
    .min(1, "The first name you have entered is too short"),
  lastName: yup
    .string()
    .required("Please enter a last name")
    .min(1, "The last name you have entered is too short")
});

export const DateOfBirthSchema = yupExtensions
  .dateTime()
  .nullable()
  .min(
    PersonMeta.getMinDateOfBirth(),
    () =>
      "The date of birth is before the minimum allowed value " +
      `(${PersonMeta.getMinDateOfBirth().toFormat(
        DATE_FORMATS.DAY_DEFAULT_FORMAT
      )})`
  );

/**
 * Validation schema for an Australian or NZ mobile phone number
 */
type CountryPhoneRegex = {
  [k in Country]: { regex: RegExp; errorMessage: string };
};

const PhoneValidation: CountryPhoneRegex = {
  AU: {
    regex: /^0[245]\d{8}$/,
    errorMessage:
      "Your mobile must start with '02', '04' or '05' and be 10 digits long"
  },
  NZ: {
    regex: /^0[245]\d{5,9}$/,
    errorMessage:
      "Your mobile must start with '02', '04' or '05' and not more than 10 digits long"
  }
};
export const MobileNumberValidationSchema = yup
  .string()
  .nullable()
  .required("Please enter a mobile number");

export const ValidatePhoneNumber = {
  name: "validate-phone-number",
  test: (value, context) => {
    if (!value) return true;

    const { parent, createError } = context;
    const country = parent?.address?.country as Country;

    const { regex, errorMessage } =
      PhoneValidation[country || Country.NewZealand];

    const isValid = new RegExp(regex).test(value);
    if (!isValid) {
      return createError({
        message: errorMessage
      });
    }

    return true;
  }
};
